import { Button, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { useFeatureFlag } from 'configcat-react';
import { debounce, isEmpty } from 'lodash';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { NavLink, withRouter } from 'react-router-dom';
import shortid from 'shortid';

import { LOGIN, ROUTES } from 'src/constants';

const NEW_ADMIN_URL = 'https://admin.pacesports.app';
import { withErrorClean } from 'hocs';

import { Input } from 'components/Fields/Input';
import { AuthSubmit } from 'components/core/Buttons';

import { intercomUtil, t, validation, oneFieldValidation } from 'utils';

import { AuthService } from '../services/auth';

import { Props, SignInState } from '../types';
import { base as ValidateConstraints } from './validations';

type InternalProps = Props & { newAuthenticationUnifiedAccounts: boolean };

const action = LOGIN;
@withRouter
@withErrorClean(action)
@inject('sessionStore')
@inject('errorsStore')
@observer
class SignIn extends React.Component<InternalProps, SignInState> {
  service: AuthService;
  id: string;

  constructor(props: InternalProps) {
    super(props);
    this.service = new AuthService();
    this.id = shortid();
  }

  componentDidMount() {
    intercomUtil.shutdown();
  }

  componentWillUnmount() {
    intercomUtil.init();
  }

  state = { email: '', password: '', signUpDialogOpen: false };

  @validation({ action, constrains: ValidateConstraints })
  validate() {
    return this.state;
  }

  debouncedValidation = debounce((name: string) => {
    const constrainsField = { [name]: ValidateConstraints[name] };

    this.props.errorsStore.clearError(action);
    oneFieldValidation(action, this.state, constrainsField);
  }, 400);

  onSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    const { email, password } = this.state;

    const isValid = this.validate();

    if (!isValid || !isEmpty(this.errors())) {
      return;
    }

    this.service.login(email, password);
  };

  onChange = (e: React.ChangeEvent) => {
    const target = e.target as HTMLInputElement;

    const name = target.name;
    const value = target.value;

    this.setState(
      {
        ...this.state,
        [name]: value,
      },
      () => this.debouncedValidation(name),
    );
  };

  errors = (): {
    [K in string]: Array<string>;
  } => {
    const { errorsStore } = this.props;
    const errors = errorsStore.errors.api[action];
    const validationErrors = errorsStore.errors.validation[action];

    if (validationErrors) {
      return validationErrors.formatErrors();
    }
    if (errors) {
      const message = t.staticAsString('login.invalidUserName');
      return { email: [message], password: [message] };
    }
    return {};
  };

  defaultInputProps = (name: string): any => ({
    name,
    onChange: this.onChange,
    id: this.id,
    value: this.state[name],
    errors: this.errors()[name],
    label: `${t.staticAsString(`login.${name}` as TranslationLockedKeys)}*`,
  });

  render() {
    const { signUpDialogOpen } = this.state;
    const { newAuthenticationUnifiedAccounts } = this.props;

    return (
      <form className='login' onSubmit={this.onSubmit}>
        <Input {...this.defaultInputProps('email')} type='email' />
        <Input {...this.defaultInputProps('password')} type='password' />
        <NavLink to={ROUTES.forgotPassword} className='forgot-password'>
          {t.staticAsString('login.forgotPassword')}
        </NavLink>
        <AuthSubmit label={t.staticAsString('login.login')} />
        <div className='login-signup-box'>
          <span className='login-signup-box--text'>{t.staticAsString('login.haveAccount.text')}</span>&nbsp;
          {newAuthenticationUnifiedAccounts ? (
            <button type='button' className='login-signup-box--link' onClick={() => this.setState({ signUpDialogOpen: true })}>
              {t.staticAsString('login.signup.link')}
            </button>
          ) : (
            <NavLink to={ROUTES.signUp} className='login-signup-box--link'>
              {t.staticAsString('login.signup.link')}
            </NavLink>
          )}
        </div>

        {newAuthenticationUnifiedAccounts && (
          <Dialog
            open={signUpDialogOpen}
            onClose={() => this.setState({ signUpDialogOpen: false })}
            maxWidth='sm'
            fullWidth
            aria-labelledby='deprecated-signup-title'
          >
            <DialogTitle id='deprecated-signup-title' sx={{ textAlign: 'center', pt: 4, pb: 1 }}>
              {t.staticAsString('login.deprecated.title')}
            </DialogTitle>
            <DialogContent sx={{ textAlign: 'center', px: 4, pb: 4 }}>
              <p style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 600, color: '#1a1a1a' }}>
                {t.staticAsString('login.deprecated.body')}
              </p>
              <p style={{ margin: '0 0 32px', fontSize: 14, color: '#555' }}>{t.staticAsString('login.deprecated.subtext')}</p>
              <Button
                variant='contained'
                size='large'
                href={NEW_ADMIN_URL}
                // target='_blank'
                // rel='noopener noreferrer'
                sx={{
                  px: 5,
                  py: 1.5,
                  fontSize: 16,
                  fontWeight: 700,
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: 3,
                }}
              >
                {t.staticAsString('login.deprecated.cta')}
              </Button>
            </DialogContent>
          </Dialog>
        )}
      </form>
    );
  }
}

const SignInWithFeatureFlag: React.FC<Props> = (props) => {
  const { value: newAuthenticationUnifiedAccounts } = useFeatureFlag('newAuthenticationUnifiedAccounts', false, {
    custom: { section: 'admin' },
    identifier: ''
  });
  return <SignIn {...(props as any)} newAuthenticationUnifiedAccounts={newAuthenticationUnifiedAccounts} />;
};

export { SignInWithFeatureFlag as SignIn };
